import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import InteractiveMap from '../components/InteractiveMap';
import { useTranslation } from '../contexts/LanguageContext';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const address = settings?.company_address || 'гр. София, п.к 1700, ж.к. Студентски град, ул. "8-ми Декември" №21';
  const phone = settings?.company_phone || '+359 890 300 600';
  const email = settings?.company_email || 'office@greencastle.bg';
  const salesPhone = settings?.sales_phone || '+359 890 150 999';
  const salesEmail = settings?.sales_email || 'sales@greencastle.bg';
  const lat = parseFloat(settings?.company_lat) || 42.6551;
  const lng = parseFloat(settings?.company_lng) || 23.3454;

  const contactInfo = [
    { icon: MapPin, title: t('contact.address'), content: address },
    { icon: Phone, title: t('contact.phone'), content: phone, href: `tel:${phone.replace(/\s/g, '')}` },
    { icon: Mail, title: t('contact.email'), content: email, href: `mailto:${email}` },
    { icon: Clock, title: t('contact.working_hours'), content: t('contact.hours_value') }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <SEO title={t('nav.contact')} description={t('seo.contact_desc')} url="/contact" />
      <Header />

      <section className="pt-32 pb-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-4">{t('contact.title')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-heading text-2xl font-bold text-foreground mb-8">{t('contact.info_title')}</h2>
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="feature-icon flex-shrink-0"><info.icon className="w-5 h-5" /></div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">{info.title}</h3>
                      {info.href ? (
                        <a href={info.href} className="text-muted-foreground hover:text-primary transition-colors">{info.content}</a>
                      ) : (
                        <p className="text-muted-foreground">{info.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-[#1B5E37]/5 border border-[#1B5E37]/20 rounded-xl">
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4">{t('contact.sales_dept')}</h3>
                <div className="space-y-3">
                  <a href={`tel:${salesPhone.replace(/\s/g, '')}`} className="flex items-center gap-3 text-muted-foreground hover:text-[#1B5E37] transition-colors">
                    <Phone className="w-4 h-4" />{salesPhone}
                  </a>
                  <a href={`mailto:${salesEmail}`} className="flex items-center gap-3 text-muted-foreground hover:text-[#1B5E37] transition-colors">
                    <Mail className="w-4 h-4" />{salesEmail}
                  </a>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="font-heading font-semibold text-lg text-foreground mb-4">{t('contact.find_us')}</h3>
                <div className="w-full h-64 rounded-xl border border-border overflow-hidden" data-testid="contact-map">
                  <InteractiveMap properties={[]} center={{ lat, lng }} zoom={16} singleMarker={true} />
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white p-8 rounded-xl border border-border shadow-sm">
                <h2 className="font-heading text-2xl font-bold text-foreground mb-2">{t('contact.form_title')}</h2>
                <p className="text-muted-foreground mb-8">{t('contact.form_subtitle')}</p>
                <InquiryForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
